import { useRef, useEffect, type ReactNode } from "react";
import { motion } from "framer-motion";
import { GradualSpacing } from "@/components/ui/gradual-spacing";
import GlobalHeader from "@/components/GlobalHeader";
import Footer from "@/components/Footer";
import { PHOTOS } from "@/lib/photos";
import { useMobileOnceInView } from "@/hooks/use-mobile-once-in-view";
import { usePageSEO } from "@/hooks/use-page-seo";

/**
 * Admirals Menu — the food program inside the Admirals Lounge, free all
 * weekend for VIP Admiral Pass holders. Stations rotate throughout the day
 * (exact times not published); everything is subject to change. Uses the
 * same poster-panel skin as /schedule.
 */

type MenuItem = {
  name: string;
  detail?: string;
};

type Station = {
  name: string;
  note?: string;
  items: MenuItem[];
};

type MenuSection = {
  key: string;
  label: string;
  sub: string;
  tone: "sky" | "butter";
  stations: Station[];
};

const SECTIONS: MenuSection[] = [
  {
    key: "grab-go",
    label: "Grab & Go",
    sub: "Standing displays — walk up any time they're out",
    tone: "sky",
    stations: [
      {
        name: "Seafood Display",
        items: [
          { name: "Jumbo Shrimp", detail: "chilled, on ice" },
          { name: "Ice Carving Centerpiece" },
        ],
      },
      {
        name: "Grand Charcuterie",
        items: [
          {
            name: "The Full Spread",
            detail: "cheeses, cured meats, breads, fruit, vegetables, crackers & dips",
          },
        ],
      },
      {
        name: "Mediterranean Station",
        items: [
          { name: "Hummus" },
          { name: "Tabouli" },
          { name: "Baba Ganoush" },
          { name: "Pita Bread", detail: "and more" },
        ],
      },
    ],
  },
  {
    key: "chef",
    label: "Chef-Attended Stations",
    sub: "Staples both days, made in front of you",
    tone: "butter",
    stations: [
      {
        name: "Crab Cakes",
        items: [
          { name: "Pan-Fried Crab Cakes", detail: "corn & arugula salad, remoulade" },
        ],
      },
      {
        name: "Lobster Rolls",
        items: [{ name: "Lobster Rolls", detail: "the Admirals Lounge classic" }],
      },
      {
        name: "Carving Station No. 1",
        items: [
          { name: "Beef Tenderloin", detail: "carved to order" },
          { name: "Tots + Garlic Aioli" },
        ],
      },
      {
        name: "Carving Station No. 2",
        items: [
          { name: "Top Round", detail: "carved to order" },
          { name: "Mashed Potatoes + Horseradish Cream" },
        ],
      },
      {
        name: "Comfort Kitchen",
        items: [
          { name: "Fried Chicken" },
          { name: "White Cheddar Mac & Cheese" },
          { name: "Coleslaw" },
        ],
      },
      {
        name: "Chicken Lolli Waffles",
        items: [
          {
            name: "Chicken & Waffle Lollipops",
            detail: "hot honey maple syrup, bacon & powdered sugar",
          },
        ],
      },
      {
        name: "Cabo Cantina",
        note: "build-your-own tacos",
        items: [
          {
            name: "Corn Tortillas",
            detail:
              "barbacoa beef, chicken or sautéed vegetables — queso fresco, cilantro, white onion",
          },
        ],
      },
    ],
  },
  {
    key: "dessert",
    label: "Dessert Dock",
    sub: "Sweets, spins & a little bourbon fire",
    tone: "sky",
    stations: [
      {
        name: "Ice Cream Cart",
        items: [{ name: "Ice Cream & Frozen Snacks", detail: "grab & go" }],
      },
      {
        name: "Popcorn Machine",
        items: [{ name: "Self-Serve Popcorn", detail: "full toppings bar" }],
      },
      {
        name: "Cotton Candy",
        items: [{ name: "Spin Your Own" }],
      },
      {
        name: "Bananas Foster Station",
        items: [
          {
            name: "Bananas Foster",
            detail: "flambéed with Howler Head bourbon, over vanilla ice cream",
          },
        ],
      },
      {
        name: "Spiked Milkshake Station",
        items: [
          {
            name: "Spiked Milkshakes",
            detail: "vanilla ice cream + Skrewball peanut butter whiskey",
          },
        ],
      },
    ],
  },
  {
    key: "late-night",
    label: "Late Night & Pop-Ups",
    sub: "Stations that appear when you least expect them",
    tone: "butter",
    stations: [
      {
        name: "Coney Station",
        note: "late night",
        items: [{ name: "Detroit Coneys" }],
      },
      {
        name: "Pretzel Wall",
        note: "late night",
        items: [{ name: "Warm Pretzels", detail: "straight off the wall" }],
      },
      {
        name: "Dumpling Cart",
        note: "pop-up",
        items: [
          {
            name: "Chicken & Veggie Dumplings",
            detail: "sweet thai chili & soy sauces",
          },
        ],
      },
      {
        name: "Fruit Market",
        note: "Saturday morning",
        items: [
          {
            name: "Fresh Fruit",
            detail:
              "apples, clementines, oranges, bananas, pears, peaches, plums, nectarines",
          },
        ],
      },
    ],
  },
];

/* ── Poster palette, lifted from the circle-design carousel (matches /schedule) ── */
const NAVY = "#14457F";
const BUTTER = "#FBF4BC";
const SKY = "#A9D4F5";
const GOLD = "#D9A62E";
const SPLASH = "#4FA8E0";

/** Sticker-style sun (matches the circle-design carousel). */
const SunSticker = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 64 64" className={className} aria-hidden>
    <g fill="#F7B41C" stroke="#FFFFFF" strokeWidth="2.5" strokeLinejoin="round">
      {Array.from({ length: 12 }, (_, i) => (
        <path
          key={i}
          d="M32 6 C34.5 12, 29.5 12, 32 18 Z"
          transform={`rotate(${i * 30} 32 32)`}
        />
      ))}
      <circle cx="32" cy="32" r="13" />
    </g>
  </svg>
);

/** Water-splash accent (matches the circle-design carousel). */
const SplashSticker = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden>
    <path d="M14 40 C10 30, 12 24, 18 18 C18 28, 17 32, 14 40 Z" fill={NAVY} />
    <path d="M24 38 C22 30, 24 25, 29 21 C28 29, 27 32, 24 38 Z" fill={SPLASH} />
    <circle cx="10" cy="20" r="2.6" fill={NAVY} />
    <circle cx="34" cy="30" r="2.2" fill={SPLASH} />
  </svg>
);

/** Gold "rope ring" poster panel with sun + splash stickers (matches /schedule). */
const PosterPanel = ({
  tone,
  children,
}: {
  tone: "sky" | "butter";
  children: ReactNode;
}) => (
  <div
    className="rounded-[1.6rem] md:rounded-[2.2rem] p-1.5 md:p-2"
    style={{ background: tone === "sky" ? SKY : BUTTER, border: `3px solid ${GOLD}` }}
  >
    <div
      className="relative rounded-[1.15rem] md:rounded-[1.7rem] px-3.5 py-4 sm:px-5 md:px-8 md:py-7 overflow-hidden"
      style={{ border: `2px dashed ${GOLD}99` }}
    >
      <SunSticker className="absolute top-3 right-3 w-12 h-12 md:w-16 md:h-16 md:top-5 md:right-5 pointer-events-none" />
      <SplashSticker className="absolute bottom-2 left-1.5 w-10 h-10 md:w-14 md:h-14 opacity-70 pointer-events-none" />
      {children}
    </div>
  </div>
);

const StationRow = ({ station }: { station: Station }) => (
  <div
    className="relative z-10 py-4 md:py-5 border-b last:border-b-0"
    style={{ borderColor: `${NAVY}26` }}
  >
    <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
      <h3
        className="font-['Anton'] uppercase tracking-wide text-xl md:text-3xl leading-none"
        style={{ color: NAVY }}
      >
        {station.name}
      </h3>
      {station.note && (
        <span
          className="text-[10.5px] md:text-xs font-bold uppercase tracking-[0.14em]"
          style={{ color: `${NAVY}8C` }}
        >
          {station.note}
        </span>
      )}
    </div>
    <div className="mt-2 space-y-1">
      {station.items.map((item) => (
        <p key={item.name} className="text-[15px] md:text-[17px] leading-snug">
          <span className="font-bold" style={{ color: NAVY }}>
            {item.name}
          </span>
          {item.detail && (
            <span style={{ color: `${NAVY}B3` }}> — {item.detail}</span>
          )}
        </p>
      ))}
    </div>
  </div>
);

const AdmiralsMenu = () => {
  usePageSEO({
    title: "Admirals Menu — Yacht Rock Detroit 2026 | Free Food All Weekend for VIP",
    description:
      "The Admirals Lounge food menu at Yacht Rock Detroit 2026 — lobster rolls, carved beef tenderloin, crab cakes, a dessert dock and late-night coneys, all free all weekend with the VIP Admiral Pass. July 17–18 at The Roostertail.",
    keywords:
      "yacht rock detroit vip food, admirals lounge menu, yacht rock detroit admiral pass, roostertail festival vip, detroit festival vip open bar",
    ogImage: PHOTOS.GROUP.fourFriendsDrinks,
    breadcrumbs: [
      { name: "Home", url: "https://yachtrockdetroit.com/" },
      { name: "Admirals Menu", url: "https://yachtrockdetroit.com/admirals-menu" },
    ],
  });

  const headerRef = useRef<HTMLElement>(null);
  const isHeaderInView = useMobileOnceInView(headerRef, { margin: "-100px" });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, []);

  return (
    <div className="min-h-screen bg-yacht-cream">
      <GlobalHeader heroRef={headerRef} />

      {/* HERO */}
      <section
        ref={headerRef}
        className="relative bg-yacht-blue overflow-hidden pt-28 md:pt-40 pb-12 md:pb-16"
      >
        <div
          className="absolute inset-0 opacity-[0.12] pointer-events-none"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-8 text-center">
          <motion.p
            className="text-yacht-yellow/80 text-sm font-medium tracking-[0.3em] uppercase mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={
              isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }
            }
            transition={{ duration: 0.6 }}
          >
            July 17–18 · Admirals Lounge · The Roostertail
          </motion.p>
          <div
            className="text-[10vw] md:text-7xl font-bold text-white leading-[0.95] tracking-tight"
            role="heading"
            aria-level={1}
          >
            <GradualSpacing text="Admirals Menu" duration={0.5} delayMultiple={0.04} />
          </div>
          <motion.p
            className="text-white/85 text-base md:text-lg mt-5 max-w-xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 16 }}
            animate={
              isHeaderInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }
            }
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Every station below is free all weekend with the VIP Admiral
            Pass — along with the open bar. Stations rotate in and out
            throughout the day, so something new is always coming off the
            line. Menu subject to change.
          </motion.p>
          <motion.p
            className="text-yacht-yellow font-bold text-xs md:text-sm tracking-[0.22em] uppercase mt-5"
            initial={{ opacity: 0 }}
            animate={isHeaderInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
          >
            Free food all weekend · Open bar · VIP Admiral Pass only
          </motion.p>
        </div>
      </section>

      {/* MENU */}
      <section className="py-10 md:py-16 px-5 sm:px-6 md:px-8">
        <div className="max-w-3xl mx-auto space-y-8 md:space-y-12">
          {SECTIONS.map((section) => (
            <div key={section.key}>
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-3 md:mb-4 px-1">
                <h2
                  className="font-['Anton'] uppercase tracking-wide text-3xl md:text-5xl leading-none"
                  style={{ color: NAVY }}
                >
                  {section.label}
                </h2>
                <span
                  className="text-[11px] md:text-sm font-semibold"
                  style={{ color: `${NAVY}8C` }}
                >
                  {section.sub}
                </span>
              </div>
              <PosterPanel tone={section.tone}>
                {section.stations.map((station) => (
                  <StationRow key={station.name} station={station} />
                ))}
              </PosterPanel>
            </div>
          ))}

          {/* Footnotes */}
          <p className="text-yacht-charcoal/45 text-xs md:text-sm leading-relaxed">
            All menu items are subject to change. Food is rotated throughout
            the day — exact station times aren't published, and the chefs
            like a surprise. Admirals Lounge is open to VIP Admiral Pass
            holders only.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="/schedule"
              className="inline-flex items-center justify-center px-8 py-4 rounded-full border-2 border-yacht-charcoal text-yacht-charcoal font-bold text-base hover:bg-yacht-charcoal hover:text-yacht-cream transition-colors"
            >
              See the Schedule
            </a>
            <a
              href="/tickets"
              className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-yacht-yellow text-yacht-charcoal font-bold text-base hover:brightness-110 transition-all"
              style={{ boxShadow: "0 4px 14px hsla(54, 93%, 49%, 0.4)" }}
            >
              Get the Admiral Pass →
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AdmiralsMenu;
